import AppData from 'Data/AppData';
import LineItemResourceData from 'Data/LineItemResourceData';
import { LINE_ITEM_STATUS } from 'Pages/Transactions/Enum';
import { areEquivalent } from 'Util';
import GenericTableData from './GenericTableData';

// Line Item Module Event Bus. Handles all interactions with Line Items.
let LineItemData = GenericTableData.getTableDataBus('LI', 'line_items');

const getSimpleLineItem = (
  {
    quantity,
    unit_price,
    measure_unit,
    vendor_catalog_number,
    description,
    comment,
    vendor_site_url,
    is_radioactive,
    assigned_transaction_id,
    source_request_id,
    shipping_id,
  },
  includeAssociationIds = false,
) => {
  return {
    quantity,
    unit_price,
    measure_unit,
    vendor_catalog_number,
    description,
    comment,
    vendor_site_url,
    is_radioactive,
    ...(includeAssociationIds && {
      assigned_transaction_id,
      source_request_id,
      shipping_id,
    }),
  };
};

LineItemData.getSimpleLineItem = getSimpleLineItem;

LineItemData.markLinesReceived = (lines) => {
  let lineItems = Array.isArray(lines) ? lines : [lines];

  let updatedLines = lineItems
    .filter(
      (item) =>
        !item.date_received || item.status !== LINE_ITEM_STATUS.RECEIVED.key,
    )
    .map((lineItem) => {
      return {
        id: lineItem.id,
        status: LINE_ITEM_STATUS.RECEIVED.key,
        quantity_received: lineItem.quantity,
        date_received: new Date(),
        received_by: AppData.userID(),
      };
    });

  let lineItemUpdates = updatedLines.map((updatedLine) => {
    return LineItemData.updateRecord(updatedLine);
  });

  return Promise.all(lineItemUpdates).then(() => {
    LineItemData.notifyListeners('LINE_ITEMS_MARKED_RECEIVED', updatedLines);
    return updatedLines;
  });
};

const getUpdateOrCreateLineItemsFunc = (lineItemDataBus) => {
  return async (linesToSave, listenerName, includeResources) => {
    let lineResourcesToDelete = [];
    let lineResourcesToCreate = [];

    let updatedLineItems = await lineItemDataBus.updateOrCreateRecords(
      linesToSave,
    );

    if (!includeResources) {
      lineItemDataBus.notifyListeners(listenerName, updatedLineItems);
      return updatedLineItems;
    }

    for (const line of updatedLineItems) {
      let currentLine = await LineItemData.getRecord(line.id, {
        _include: 'resources',
      });
      let currentResources = currentLine.resources.map((r) => r.id);

      let inputLine = linesToSave.find(
        (l) =>
          (l.id && l.id === currentLine.id) ||
          areEquivalent(
            getSimpleLineItem(currentLine, false),
            getSimpleLineItem(l, false),
          ),
      );
      let newResources = inputLine?.resources.map((r) => r.id);

      let resourcesToDel = currentResources
        .filter((r) => !newResources.includes(r))
        .map((r) => {
          return { resource_id: r, line_item_id: currentLine.id };
        });
      lineResourcesToDelete.push(...resourcesToDel);

      let resourcesToAdd = newResources
        .filter((r) => !currentResources.includes(r))
        .map((r) => {
          return { resource_id: r, line_item_id: currentLine.id };
        });
      lineResourcesToCreate.push(...resourcesToAdd);

      line.resources = inputLine?.resources || [];
    }

    let delPromises = lineResourcesToDelete.map(async (lir) => {
      return await LineItemResourceData.deleteRecordsWhere(lir);
    });

    let addPromises = lineResourcesToCreate.map(async (lir) => {
      return await LineItemResourceData.createRecord(lir);
    });

    return Promise.all([...delPromises, ...addPromises]).then((results) => {
      lineItemDataBus.notifyListeners(listenerName, updatedLineItems);
      return updatedLineItems;
    });
  };
};

const GetLineItemDataBus = () => {
  let LineItemData = GenericTableData.getTableDataBus('LI', 'line_items');

  LineItemData.updateOrCreateLineItems =
    getUpdateOrCreateLineItemsFunc(LineItemData);

  return LineItemData;
};

export { LineItemData, GetLineItemDataBus };
